/**
 * ProductForm Component
 *
 * Form for creating/editing products with Dataverse field mappings
 */

import { useEffect, useState } from 'react';
import { Form, Input, InputNumber, Row, Col, Select, Upload } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import type { UploadFile, UploadProps } from 'antd';
import { toast } from 'react-hot-toast';
import { PRODUCT_STRUCTURE_TYPES } from '../../../constants';

interface ProductFormProps {
  existingImageUrl?: string;
  existingWithHornsUrl?: string;
  existingWithoutHornsUrl?: string;
}

export const ProductForm = ({ existingImageUrl, existingWithHornsUrl, existingWithoutHornsUrl }: ProductFormProps) => {
  const form = Form.useFormInstance();
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [fileListWithHorns, setFileListWithHorns] = useState<UploadFile[]>([]);
  const [fileListWithoutHorns, setFileListWithoutHorns] = useState<UploadFile[]>([]);

  // Initialize file lists from existing product images (edit mode)
  useEffect(() => {
    if (existingImageUrl) {
      const file: UploadFile = { uid: '-1', name: 'product-image', status: 'done', url: existingImageUrl };
      setFileList([file]);
      form.setFieldValue('productImage', [file]);
    } else {
      setFileList([]);
    }

    if (existingWithHornsUrl) {
      const file: UploadFile = { uid: '-2', name: 'product-with-horns', status: 'done', url: existingWithHornsUrl };
      setFileListWithHorns([file]);
      form.setFieldValue('productImageWithHorns', [file]);
    } else {
      setFileListWithHorns([]);
    }

    if (existingWithoutHornsUrl) {
      const file: UploadFile = { uid: '-3', name: 'product-without-horns', status: 'done', url: existingWithoutHornsUrl };
      setFileListWithoutHorns([file]);
      form.setFieldValue('productImageWithoutHorns', [file]);
    } else {
      setFileListWithoutHorns([]);
    }
  }, [existingImageUrl, existingWithHornsUrl, existingWithoutHornsUrl]);
  const handleImageChange: UploadProps['onChange'] = ({ fileList: newFileList }) => {
    setFileList(newFileList);
    form.setFieldValue('productImage', newFileList);
  };

  const handleImageWithHornsChange: UploadProps['onChange'] = ({ fileList: newFileList }) => {
    setFileListWithHorns(newFileList);
    form.setFieldValue('productImageWithHorns', newFileList);
  };

  const handleImageWithoutHornsChange: UploadProps['onChange'] = ({ fileList: newFileList }) => {
    setFileListWithoutHorns(newFileList);
    form.setFieldValue('productImageWithoutHorns', newFileList);
  };

  const beforeUpload = (file: File) => {
    const isImage = file.type.startsWith('image/');
    if (!isImage) {
      toast.error('You can only upload image files!');
      return false;
    }
    const isLt5M = file.size / 1024 / 1024 < 5;
    if (!isLt5M) {
      toast.error('Image must be smaller than 5MB!');
      return false;
    }
    return false; // Prevent auto upload, handle manually
  };

  const uploadButton = (
    <div>
      <PlusOutlined />
      <div style={{ marginTop: 8 }}>Upload</div>
    </div>
  );

  return (
    <div className="pt-4 w-full overflow-x-hidden">
      <Row gutter={16}>
        <Col span={12}>
          <Form.Item
            label="Product Name"
            name="name"
            rules={[{ required: true, message: 'Please enter product name' }]}
          >
            <Input placeholder="Enter product name" />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item
            label="Product Code"
            name="productnumber"
          >
            <Input placeholder="Enter product code" />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={16}>
        <Col span={12}>
          <Form.Item
            label="Product Type"
            name="producttypecode"
            rules={[{ required: true, message: 'Please select product type' }]}
          >
            <Select placeholder="Select product type">
              {PRODUCT_STRUCTURE_TYPES.map((type) => (
                <Select.Option key={type.value} value={type.value}>
                  {type.label}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item
            label="Number of Window Frames"
            name="quantityonhand"
          >
            <InputNumber
              placeholder="Enter number of frames"
              min={1}
              style={{ width: '100%' }}
            />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={16}>
        <Col span={12}>
          <Form.Item
            label="Base Price (£)"
            name="price"
            rules={[{ required: true, message: 'Please enter base price' }]}
          >
            <InputNumber
              placeholder="0.00"
              min={0}
              precision={2}
              style={{ width: '100%' }}
            />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item
            label="Unglazed Price (£)"
            name="dogma_unglazedprice"
            tooltip="Price per frame when the Unglazed glazing option is selected"
          >
            <InputNumber
              placeholder="0.00"
              min={0}
              precision={2}
              style={{ width: '100%' }}
            />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={16}>
        {/* <Col span={12}>
          <Form.Item
            label="Number of Sashes"
            name="dogma_numberofsash"
            tooltip="Number of sashes per window frame (e.g. Standard=2, Arch=1)"
          >
            <InputNumber
              placeholder="Enter number of sashes"
              min={1}
              style={{ width: '100%' }}
            />
          </Form.Item>
        </Col> */}
      </Row>

      <Form.Item
        label="Description"
        name="description"
      >
        <Input.TextArea placeholder="Enter description" rows={3} />
      </Form.Item>

      {import.meta.env.VITE_ENV === 'qa' && (
        <Form.Item
          label="Window JSON (dogma_proudctwindowjson)"
          name="dogma_proudctwindowjson"
          rules={[
            {
              validator: (_, value) => {
                if (!value || value.trim() === '') return Promise.resolve();
                try {
                  const stripped = value
                    .replace(/\/\/[^\n]*/g, '')
                    .replace(/\/\*[\s\S]*?\*\//g, '');
                  JSON.parse(stripped);
                  return Promise.resolve();
                } catch {
                  return Promise.reject(new Error('Invalid JSON — check syntax'));
                }
              },
            },
          ]}
        >
          <Input.TextArea
            placeholder={`[\n  {\n    "bay_side": "l",\n    "perspective_corners": [\n      { "x": 104, "y": 819 }\n    ]\n  }\n]`}
            rows={8}
            style={{ fontFamily: 'monospace', fontSize: 12 }}
          />
        </Form.Item>
      )}

      <Row gutter={16}>
        <Col span={8}>
          <Form.Item
            label="Product Image"
            name="productImage"
            valuePropName="fileList"
            getValueFromEvent={(e) => {
              if (Array.isArray(e)) return e;
              return e?.fileList;
            }}
            rules={[{ required: true, message: 'Please upload a product image' }]}
          >
            <Upload
              listType="picture-card"
              fileList={fileList}
              onChange={handleImageChange}
              beforeUpload={beforeUpload}
              maxCount={1}
              accept="image/*"
            >
              {fileList.length >= 1 ? null : uploadButton}
            </Upload>
          </Form.Item>
        </Col>
        {/* <Col span={8}>
          <Form.Item
            label="Product With Horns"
            name="productImageWithHorns"
            valuePropName="fileList"
            getValueFromEvent={(e) => {
              if (Array.isArray(e)) return e;
              return e?.fileList;
            }}
            rules={[{ required: true, message: 'Please upload a product with horns image' }]}
          >
            <Upload
              listType="picture-card"
              fileList={fileListWithHorns}
              onChange={handleImageWithHornsChange}
              beforeUpload={beforeUpload}
              maxCount={1}
              accept="image/*"
            >
              {fileListWithHorns.length >= 1 ? null : uploadButton}
            </Upload>
          </Form.Item>
        </Col> */}
        {/* <Col span={8}>
          <Form.Item
            label="Product Without Horns"
            name="productImageWithoutHorns"
            valuePropName="fileList"
            getValueFromEvent={(e) => {
              if (Array.isArray(e)) return e;
              return e?.fileList;
            }}
            rules={[{ required: true, message: 'Please upload a product without horns image' }]}
          >
            <Upload
              listType="picture-card"
              fileList={fileListWithoutHorns}
              onChange={handleImageWithoutHornsChange}
              beforeUpload={beforeUpload}
              maxCount={1}
              accept="image/*"
            >
              {fileListWithoutHorns.length >= 1 ? null : uploadButton}
            </Upload>
          </Form.Item>
        </Col> */}
      </Row>
    </div>
  );
};
